/**
 * Conflict Resolution Component for CSV Import Review
 *
 * Shows one conflict at a time with navigation and resolution options.
 * Users choose between CSV value or existing value for each conflict.
 */

import { useQueries } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { ProgressBarBase } from '@/lib/base/progress-indicators';
import { RadioCard, RadioGroup } from '@/lib/base/radio-buttons/radio-buttons';
import { Tooltip } from '@/lib/base/tooltip';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type {
  ConflictItem,
  ConflictResolutions,
  CsvImport,
} from '@/lib/csv-import/api';

import { getFileDisplayName } from '../utils/fileDisplayName';
import { fetchPropertyTypes, getPropertyLabel } from './summary/types';
import { getEntityRowDisplayName } from './summaryHelpers';

// ============================================================================
// Helper Functions
// ============================================================================

function isBlankValue(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }
  return false;
}

// ============================================================================
// Types
// ============================================================================

export interface ConflictResolutionProps {
  importData: CsvImport;
  resolutions: ConflictResolutions;
  onResolutionsChange: (resolutions: ConflictResolutions) => void;
  onBack: () => void;
  onApprove: () => void;
  isApproving: boolean;
  orgId: number;
}

// ============================================================================
// Subcomponents
// ============================================================================

interface ConflictCardProps {
  conflict: ConflictItem;
  filename: string;
  /** Display name of the record being changed, when it can be resolved from the
   *  analyzed row. Null on large imports where rows aren't sent inline. */
  recordName: string | null;
  resolution: 'csv' | 'existing' | undefined;
  onResolve: (choice: 'csv' | 'existing') => void;
  schemaMap: PropertySchemaMap;
}

function ConflictCard({
  conflict,
  filename,
  recordName,
  resolution,
  onResolve,
  schemaMap,
}: ConflictCardProps) {
  // RadioGroup hands back a bare `string`. Narrow it here rather than widening
  // `onResolve` — the caller's contract really is one of two choices, and a
  // `string` there would let any value through to the resolutions map.
  const handleChange = useCallback(
    (value: string) => {
      if (value === 'csv' || value === 'existing') {
        onResolve(value);
      }
    },
    [onResolve]
  );

  const propertyType = schemaMap[conflict.property_name];
  const csvBlank = isBlankValue(conflict.csv_value);
  const existingBlank = isBlankValue(conflict.existing_value);
  const csvMostComplete = !csvBlank && existingBlank;
  const existingMostComplete = !existingBlank && csvBlank;

  // Batch-dedup conflicts compare two rows from the same CSV (e.g. row 1
  // says "Software", row 3 says "Hardware"), so the generic "CSV Value" /
  // "Existing Value" labels mislead — both came from the CSV. Swap to
  // row references when we have them. Backend sets both indices in the
  // batch-dedup path; in the unlikely case one is missing, fall back to
  // the generic labels.
  const isBatchDedup = Boolean(conflict.is_batch_dedup);
  const csvRowLabel =
    isBatchDedup && conflict.csv_row_index !== undefined
      ? `Row ${conflict.csv_row_index + 1}`
      : 'CSV Value';
  const existingRowLabel =
    isBatchDedup && conflict.existing_row_index !== undefined
      ? `Row ${conflict.existing_row_index + 1}`
      : 'Existing Value';

  // The header row reference: for batch-dedup we point at the row that
  // first claimed the dedup group (existing_row_index) so the user knows
  // where the merge is happening. For non-batch conflicts we point at
  // the CSV row that conflicted with the existing CRM record.
  const headerRowIndex =
    isBatchDedup && conflict.existing_row_index !== undefined
      ? conflict.existing_row_index
      : conflict.row_index;

  return (
    <div className='border border-secondary rounded-lg overflow-hidden'>
      {/* Header */}
      <div className='bg-bg-secondary px-4 py-3 border-b border-secondary'>
        {/* The record's name leads, because "whose data am I overwriting?" is
            the question this screen has to answer. Filename + row stays as
            locating context, demoted. When the name can't be resolved the
            filename takes the lead again, exactly as before. */}
        <div className='flex items-center gap-2 text-sm flex-wrap'>
          <span className='font-medium text-primary'>
            {recordName ?? filename}
          </span>
          <span className='text-tertiary'>·</span>
          <span className='text-tertiary'>
            {recordName ? `${filename}, row ` : 'Row '}
            {headerRowIndex + 1}
          </span>
          {isBatchDedup && (
            <Badge color='gray' size='sm'>
              Duplicate
            </Badge>
          )}
        </div>
        {isBatchDedup && (
          <p className='text-xs text-tertiary mt-1'>
            Duplicate rows with conflicting values for this property. Pick the
            value to keep.
          </p>
        )}
      </div>

      {/* Property conflict */}
      <div className='p-4'>
        <div className='text-sm font-medium text-primary mb-3'>
          {getPropertyLabel(conflict.property_name, schemaMap, {})}
        </div>

        {/* Two mutually-exclusive choices, so a radio group — the previous pair
            of plain buttons exposed no selected state to a screen reader, and
            offered no arrow-key navigation between the options. */}
        <RadioGroup
          value={resolution ?? null}
          onChange={handleChange}
          aria-label={`Choose which value to keep for ${getPropertyLabel(conflict.property_name, schemaMap, {})}`}
          className='grid grid-cols-1 sm:grid-cols-2 gap-4'
        >
          <RadioCard value='csv' showIndicator>
            <div className='flex items-center gap-2'>
              <span className='text-xs text-tertiary'>{csvRowLabel}</span>
              {csvMostComplete && (
                <span className='text-xs font-medium text-utility-success-700'>
                  Most complete
                </span>
              )}
            </div>
            <div className='text-sm font-medium text-primary break-words'>
              <PropertyValueLabel
                value={conflict.csv_value}
                propertyType={propertyType}
                fontSize='sm'
              />
            </div>
          </RadioCard>

          <RadioCard value='existing' showIndicator>
            <div className='flex items-center gap-2'>
              <span className='text-xs text-tertiary'>{existingRowLabel}</span>
              {existingMostComplete && (
                <span className='text-xs font-medium text-utility-success-700'>
                  Most complete
                </span>
              )}
            </div>
            <div className='text-sm font-medium text-primary break-words'>
              <PropertyValueLabel
                value={conflict.existing_value}
                propertyType={propertyType}
                fontSize='sm'
              />
            </div>
          </RadioCard>
        </RadioGroup>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ConflictResolution({
  importData,
  resolutions,
  onResolutionsChange,
  onBack,
  onApprove,
  isApproving,
  orgId,
}: ConflictResolutionProps) {
  // Get conflicts from analysis result
  const conflicts = useMemo(() => {
    return importData.analysis_result?.conflicts || [];
  }, [importData.analysis_result]);

  // Get unique entity type names from uploaded files
  const entityTypeNames = useMemo(() => {
    const types = new Set<string>();
    for (const file of importData.uploaded_files) {
      types.add(file.entity_type_name);
    }
    return Array.from(types);
  }, [importData.uploaded_files]);

  // Fetch property schemas for all entity types using useQueries
  const schemaQueries = useQueries({
    queries: entityTypeNames.map(typeName => ({
      queryKey: ['propertySchema', orgId, typeName],
      queryFn: () => fetchPropertyTypes(orgId, typeName),
      staleTime: 5 * 60 * 1000,
    })),
  });

  // Combine all schemas into a single lookup map
  const schemaMap = useMemo(() => {
    const map: PropertySchemaMap = {};
    for (const query of schemaQueries) {
      if (query.data) {
        for (const pt of query.data) {
          map[pt.property_type_name] = pt;
        }
      }
    }
    return map;
  }, [schemaQueries]);

  const schemaError = schemaQueries.some(q => q.isError);

  // Track current conflict index for navigation
  const [navIndex, setNavIndex] = useState(() => {
    // Find first unresolved conflict on initial render
    const firstUnresolved = conflicts.findIndex(c => !resolutions[c.key]);
    return firstUnresolved >= 0 ? firstUnresolved : 0;
  });

  const currentConflict = conflicts[navIndex];
  const resolvedCount = conflicts.filter(c => resolutions[c.key]).length;
  const allResolved = resolvedCount === conflicts.length;

  // The name of the record the current conflict will change.
  //
  // Filename + row number tells a user *where* the conflict is but not *whose*
  // data they are about to overwrite, which is the thing they need in order to
  // decide. Resolved from the analyzed row via the same helper the association
  // tables use.
  //
  // Analyzed rows are omitted from the detail response above
  // `csv_imports.review_inline_row_threshold`, so this is best-effort: when the
  // row isn't inline we fall back to filename + row rather than blocking on a
  // fetch, and the header reads exactly as it does today.
  const currentRecordName = useMemo(() => {
    if (!currentConflict) {
      return null;
    }
    const fileResult = (importData.analysis_result ?? {})[
      String(currentConflict.file_id)
    ];
    if (
      typeof fileResult !== 'object' ||
      fileResult === null ||
      !('analyzed_rows' in fileResult)
    ) {
      return null;
    }
    const rows = (
      fileResult as {
        analyzed_rows?: Array<{
          row_index: number;
          data?: Record<string, unknown>;
          raw_data?: Record<string, unknown>;
        }>;
      }
    ).analyzed_rows;
    const row = rows?.find(r => r.row_index === currentConflict.row_index);
    if (!row) {
      return null;
    }
    const entityTypeName =
      importData.uploaded_files.find(
        f => String(f.file_id) === String(currentConflict.file_id)
      )?.entity_type_name ?? '';
    return getEntityRowDisplayName(
      row.data ?? {},
      row.raw_data ?? {},
      entityTypeName
    );
  }, [currentConflict, importData.analysis_result, importData.uploaded_files]);

  // Navigate to previous conflict
  const handlePrev = useCallback(() => {
    setNavIndex(prev => Math.max(0, prev - 1));
  }, []);

  // Navigate to next conflict
  const handleNext = useCallback(() => {
    setNavIndex(prev => Math.min(conflicts.length - 1, prev + 1));
  }, [conflicts.length]);

  // Resolve current conflict
  const handleResolve = useCallback(
    (choice: 'csv' | 'existing') => {
      if (!currentConflict) {
        return;
      }

      const newResolutions = {
        ...resolutions,
        [currentConflict.key]: choice,
      };
      onResolutionsChange(newResolutions);

      // Auto-advance to next unresolved if available
      const nextUnresolved = conflicts.findIndex(
        (c, i) => i > navIndex && !newResolutions[c.key]
      );
      if (nextUnresolved >= 0) {
        setNavIndex(nextUnresolved);
      }
    },
    [currentConflict, resolutions, onResolutionsChange, conflicts, navIndex]
  );

  // Bulk resolve all as CSV
  const handleKeepAllCsv = useCallback(() => {
    const newResolutions: ConflictResolutions = {};
    conflicts.forEach(c => {
      newResolutions[c.key] = 'csv';
    });
    onResolutionsChange(newResolutions);
  }, [conflicts, onResolutionsChange]);

  // Bulk resolve all as existing
  const handleKeepAllExisting = useCallback(() => {
    const newResolutions: ConflictResolutions = {};
    conflicts.forEach(c => {
      newResolutions[c.key] = 'existing';
    });
    onResolutionsChange(newResolutions);
  }, [conflicts, onResolutionsChange]);

  // Bulk resolve: prefer existing, but use CSV where existing is blank
  const handleFillGaps = useCallback(() => {
    const newResolutions: ConflictResolutions = {};
    conflicts.forEach(c => {
      newResolutions[c.key] = isBlankValue(c.existing_value)
        ? 'csv'
        : 'existing';
    });
    onResolutionsChange(newResolutions);
  }, [conflicts, onResolutionsChange]);

  // Empty state
  if (conflicts.length === 0) {
    return (
      <div className='py-8 text-center'>
        <p className='text-sm text-tertiary'>No conflicts to resolve</p>
        <Button color='secondary' size='sm' className='mt-4' onClick={onBack}>
          Back to Summary
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Progress Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <AlertTriangle className='w-5 h-5 text-utility-warning-600' />
          <span className='text-sm font-medium text-primary'>
            {resolvedCount} of {conflicts.length} resolved
          </span>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-tertiary'>
            Conflict {navIndex + 1} of {conflicts.length}
          </span>
        </div>
      </div>

      {/* Resolution progress. The design-system bar also exposes
          role="progressbar" with the value, which the hand-rolled pair of divs
          did not — the count was visible but never announced. */}
      <ProgressBarBase value={resolvedCount} max={conflicts.length} />

      {schemaError && (
        <p className='text-xs text-utility-warning-600'>
          Property labels may be incomplete – schema failed to load.
        </p>
      )}

      {/* Bulk Actions */}
      <div className='flex items-center gap-2'>
        <Tooltip
          title="Ignore CSV values where there's a conflict"
          placement='bottom'
        >
          <Button
            color='secondary'
            size='sm'
            onClick={handleKeepAllExisting}
            isDisabled={isApproving}
          >
            Keep all existing
          </Button>
        </Tooltip>
        <Tooltip
          title='Replace existing data, even if CSV fields are blank'
          placement='bottom'
        >
          <Button
            color='secondary'
            size='sm'
            onClick={handleKeepAllCsv}
            isDisabled={isApproving}
          >
            Use all CSV
          </Button>
        </Tooltip>
        <Tooltip
          title='Keep existing data and use CSV only to fill blank fields'
          placement='bottom'
        >
          <Button
            color='secondary'
            size='sm'
            onClick={handleFillGaps}
            isDisabled={isApproving}
          >
            Use most complete
          </Button>
        </Tooltip>
      </div>

      {/* Current Conflict Card */}
      {currentConflict && (
        <ConflictCard
          conflict={currentConflict}
          filename={getFileDisplayName(
            currentConflict.file_id,
            importData.uploaded_files
          )}
          recordName={currentRecordName}
          resolution={resolutions[currentConflict.key]}
          onResolve={handleResolve}
          schemaMap={schemaMap}
        />
      )}

      {/* Navigation */}
      <div className='flex items-center justify-between'>
        <Button
          color='secondary'
          size='sm'
          iconLeading={ChevronLeft}
          onClick={handlePrev}
          isDisabled={navIndex === 0 || isApproving}
        >
          Previous
        </Button>

        <Button
          color='secondary'
          size='sm'
          iconTrailing={ChevronRight}
          onClick={handleNext}
          isDisabled={navIndex === conflicts.length - 1 || isApproving}
        >
          Next
        </Button>
      </div>

      {/* Footer Actions */}
      <div className='flex items-center justify-between border-t border-secondary pt-4'>
        <Button
          color='tertiary'
          size='sm'
          onClick={onBack}
          isDisabled={isApproving}
        >
          Back to Summary
        </Button>

        <Button
          color='primary'
          size='lg'
          onClick={onApprove}
          isDisabled={!allResolved || isApproving}
          isLoading={isApproving}
        >
          Approve & Import
        </Button>
      </div>

      {/* Help text */}
      {!allResolved && (
        <p className='text-xs text-tertiary text-right'>
          Resolve all conflicts to enable the Approve button
        </p>
      )}
    </div>
  );
}
